import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import {
  FormFieldInput,
  FormFieldTextarea,
  FormFieldSelect,
} from '@/shared/components/custom/form-field';
import { AsyncCombobox } from '@/shared/components/custom/async-combobox/AsyncCombobox';
import { cn } from '@/core/lib/utils';
import type { ConvertToTaskFormValues } from '../schemas';
import type { NoteTaskProposal } from '../types';
import type { NameMatch } from '../utils/matchOptionByName';
import { nameHint } from '../utils/convertToTaskHints';
import { ConvertToTaskField } from './ConvertToTaskField';

const AI_FILLED = 'border-primary';
const FIELD_ICON = 'size-4 shrink-0 text-muted-foreground';

interface ConvertToTaskIdentitySectionProps {
  control: Control<ConvertToTaskFormValues>;
  isPending: boolean;
  proposal: NoteTaskProposal | null;
  organizationOptions: { value: string; label: string }[];
  fetchProjects: (args: {
    search: string;
    pageSize: number;
  }) => Promise<{ items: { id: string; label: string }[]; total: number }>;
  selectedProjectOption: { value: string; label: string } | undefined;
  projectMatch: NameMatch;
  projectCandidatesCount: number;
  proposedProject: string | null;
}

/** Title, description, organization and project — the fields every task needs. */
export function ConvertToTaskIdentitySection({
  control,
  isPending,
  proposal,
  organizationOptions,
  fetchProjects,
  selectedProjectOption,
  projectMatch,
  projectCandidatesCount,
  proposedProject,
}: ConvertToTaskIdentitySectionProps) {
  const { t } = useTranslation('notes');
  const aiSuggested = t('convertToTask.aiSuggested');
  const aiShort = t('convertToTask.aiShort');

  return (
    <>
      <ConvertToTaskField
        label={t('convertToTask.fields.name')}
        required
        aiLabel={proposal?.title ? aiSuggested : undefined}
        htmlFor="title"
      >
        <FormFieldInput
          control={control}
          name="title"
          label={t('convertToTask.fields.name')}
          disabled={isPending}
          placeholder={t('convertToTask.fields.namePlaceholder')}
          className={cn('h-10', proposal?.title && AI_FILLED)}
          hideLabel
          data-testid="notes-convert-name-input"
        />
      </ConvertToTaskField>

      <ConvertToTaskField
        label={t('convertToTask.fields.description')}
        aiLabel={proposal?.description ? aiSuggested : undefined}
        htmlFor="description"
      >
        <FormFieldTextarea
          control={control}
          name="description"
          label={t('convertToTask.fields.description')}
          disabled={isPending}
          placeholder={t('convertToTask.fields.descriptionPlaceholder')}
          className={cn('min-h-[92px] resize-none', proposal?.description && AI_FILLED)}
          rows={4}
          hideLabel
          data-testid="notes-convert-description-input"
        />
      </ConvertToTaskField>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Locked to the active organization: the task is created in whatever
            organization the session is on — the tenant header comes from the store,
            not from this form — so offering a choice here promised something the
            request could not keep. Switching organizations is a session-level action. */}
        <ConvertToTaskField
          label={t('convertToTask.fields.organization')}
          required
          htmlFor="organizationId"
          hint={t('convertToTask.fields.organizationLocked')}
        >
          <FormFieldSelect
            control={control}
            name="organizationId"
            label={t('convertToTask.fields.organization')}
            options={organizationOptions}
            placeholder={t('convertToTask.fields.organizationPlaceholder')}
            startIcon={<Building2 className={FIELD_ICON} aria-hidden />}
            disabled
            hideLabel
            data-testid="notes-convert-org-select"
          />
        </ConvertToTaskField>
        {/* Searchable rather than a plain select: the list is as long as the tenant
            is, and a fixed page would put every project past it out of reach. */}
        <Controller
          name="projectId"
          control={control}
          render={({ field, fieldState }) => (
            <ConvertToTaskField
              label={t('convertToTask.fields.project')}
              required
              aiLabel={proposal?.projectName ? aiShort : undefined}
              hint={nameHint(
                t,
                projectMatch,
                proposal?.projectName,
                projectCandidatesCount,
                'convertToTask.observations.projectNotFound',
              )}
              htmlFor="projectId"
            >
              <AsyncCombobox
                queryKey={['notes-convert-projects']}
                fetchFn={fetchProjects}
                value={field.value || null}
                onChange={(value) => field.onChange(value ?? '')}
                selectedOptions={
                  selectedProjectOption
                    ? [{ id: selectedProjectOption.value, label: selectedProjectOption.label }]
                    : undefined
                }
                placeholder={t('convertToTask.fields.projectPlaceholder')}
                searchPlaceholder={t('convertToTask.fields.projectSearch')}
                emptyMessage={t('convertToTask.fields.projectEmpty')}
                disabled={isPending}
                triggerClassName={cn('h-10', proposedProject && AI_FILLED)}
                data-testid="notes-convert-project-select"
              />
              {fieldState.error?.message && (
                <span role="alert" className="text-xs text-destructive">
                  {t(fieldState.error.message)}
                </span>
              )}
            </ConvertToTaskField>
          )}
        />
      </div>
    </>
  );
}
